import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Popper, useMediaQuery } from "@mui/material";
import { getFlagUrl } from "@helpers/getFlagUrl";
import CountryIcon from "@assets/icons/CountryIcon";
import { alpha3ToAlpha2 } from "@utils/countryCodeMap";

import "./CountrySelectProfile.sass";

const DROPDOWN_GAP = 6;
const MOBILE_DROPDOWN_EXTRA_GAP = 8;

const DEFAULT_DROPDOWN_HEIGHT = 280;
const MOBILE_DROPDOWN_MAX_HEIGHT = 320;

const MOBILE_SIDE_OFFSET = 16;

const SCROLL_DIRECTION_THRESHOLD = 6;
const WHEEL_GESTURE_DELAY = 150;

const CountrySelectProfile = forwardRef(
  (
    {
      countries = [],
      value,
      onChange,
      onOpenChange,
      locale,
      placeholder = "Select country",
      error = false,
      disabled = false,
      showArrow = true,
    },
    ref,
  ) => {
    const isMobile = useMediaQuery("(max-width:460px)");

    const [open, setOpen] = useState(false);
    const [dropdownWidth, setDropdownWidth] = useState(300);

    const selectRef = useRef(null);
    const dropdownRef = useRef(null);

    const normalizeAlpha2 = (code) => {
      if (!code || typeof code !== "string") {
        return "";
      }

      return code.trim().toLowerCase();
    };

    const getCountryAlpha2 = (country) => {
      if (!country) {
        return "";
      }

      const directCode =
        country.code_alpha2 ||
        country.alpha2 ||
        country.iso2 ||
        country.code2 ||
        "";

      if (directCode) {
        return normalizeAlpha2(directCode);
      }

      if (typeof country.code === "string") {
        const code = country.code.trim();

        if (code.length === 2) {
          return normalizeAlpha2(code);
        }

        if (code.length === 3) {
          return normalizeAlpha2(
            alpha3ToAlpha2[code.toUpperCase()],
          );
        }
      }

      return "";
    };

    const getCountryLabel = (country) => {
      if (!country) {
        return "";
      }

      return locale === "ru"
        ? country.label_ru || country.label_en
        : country.label_en || country.label_ru;
    };

    const normalizedValue = normalizeAlpha2(value);

    const selected = countries.find(
      (countryItem) =>
        getCountryAlpha2(countryItem) === normalizedValue,
    );

    const dropdownMaxHeight = isMobile
      ? MOBILE_DROPDOWN_MAX_HEIGHT
      : DEFAULT_DROPDOWN_HEIGHT;

    const dropdownGap = isMobile
      ? DROPDOWN_GAP + MOBILE_DROPDOWN_EXTRA_GAP
      : DROPDOWN_GAP;

    const changeOpenState = useCallback(
      (nextOpen) => {
        setOpen((currentOpen) => {
          const resolvedOpen =
            typeof nextOpen === "function"
              ? nextOpen(currentOpen)
              : nextOpen;

          onOpenChange?.(resolvedOpen);

          return resolvedOpen;
        });
      },
      [onOpenChange],
    );

    const updateDropdownWidth = useCallback(() => {
      if (!selectRef.current) {
        return;
      }

      const rect = selectRef.current.getBoundingClientRect();

      const viewportWidth =
        document.documentElement.clientWidth ||
        window.innerWidth;

      if (isMobile) {
        setDropdownWidth(
          Math.max(
            0,
            viewportWidth - MOBILE_SIDE_OFFSET * 2,
          ),
        );

        return;
      }

      setDropdownWidth(rect.width);
    }, [isMobile]);

    const openDropdown = useCallback(() => {
      if (disabled) {
        return;
      }

      updateDropdownWidth();
      changeOpenState(true);
    }, [
      changeOpenState,
      disabled,
      updateDropdownWidth,
    ]);

    const closeDropdown = useCallback(() => {
      changeOpenState(false);
    }, [changeOpenState]);

    const toggleDropdown = useCallback(() => {
      if (disabled) {
        return;
      }

      if (!open) {
        updateDropdownWidth();
      }

      changeOpenState(
        (currentOpen) => !currentOpen,
      );
    }, [
      changeOpenState,
      disabled,
      open,
      updateDropdownWidth,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        open: openDropdown,
        close: closeDropdown,
        toggle: toggleDropdown,
      }),
      [
        closeDropdown,
        openDropdown,
        toggleDropdown,
      ],
    );

    useEffect(() => {
      if (!open) {
        return undefined;
      }

      let touchStartY = null;
      let touchGestureDirection = null;

      let wheelGestureDirection = null;
      let wheelGestureTimer = null;

      const isInsideCountrySelect = (target) => {
        if (!(target instanceof Element)) {
          return false;
        }

        const clickedInsideSelect =
          selectRef.current?.contains(target);

        const clickedInsideDropdown =
          dropdownRef.current?.contains(target);

        const clickedExternalTrigger = Boolean(
          target.closest(
            ".country-row__select-button",
          ),
        );

        return (
          clickedInsideSelect ||
          clickedInsideDropdown ||
          clickedExternalTrigger
        );
      };

      const handleOutsideClick = (event) => {
        if (isInsideCountrySelect(event.target)) {
          return;
        }

        closeDropdown();
      };

      const resetWheelGesture = () => {
        wheelGestureDirection = null;
        wheelGestureTimer = null;
      };

      const handleWheelOutside = (event) => {
        if (isInsideCountrySelect(event.target)) {
          return;
        }

        clearTimeout(wheelGestureTimer);

        wheelGestureTimer = setTimeout(
          resetWheelGesture,
          WHEEL_GESTURE_DELAY,
        );

        if (
          Math.abs(event.deltaY) <
          SCROLL_DIRECTION_THRESHOLD
        ) {
          return;
        }

        if (!wheelGestureDirection) {
          wheelGestureDirection =
            event.deltaY < 0 ? "up" : "down";
        }

        if (wheelGestureDirection === "up") {
          closeDropdown();
        }
      };

      const handleTouchStartOutside = (event) => {
        if (isInsideCountrySelect(event.target)) {
          touchStartY = null;
          touchGestureDirection = null;
          return;
        }

        touchStartY =
          event.touches[0]?.clientY ?? null;

        touchGestureDirection = null;
      };

      const handleTouchMoveOutside = (event) => {
        if (isInsideCountrySelect(event.target)) {
          return;
        }

        const currentTouchY =
          event.touches[0]?.clientY;

        if (
          typeof currentTouchY !== "number" ||
          typeof touchStartY !== "number"
        ) {
          return;
        }

        const touchDelta =
          currentTouchY - touchStartY;

        if (
          Math.abs(touchDelta) <
          SCROLL_DIRECTION_THRESHOLD
        ) {
          return;
        }

        if (!touchGestureDirection) {
          touchGestureDirection =
            touchDelta > 0 ? "up" : "down";
        }

        if (touchGestureDirection === "up") {
          closeDropdown();
        }
      };

      const handleTouchEnd = () => {
        touchStartY = null;
        touchGestureDirection = null;
      };

      document.addEventListener(
        "mousedown",
        handleOutsideClick,
      );

      document.addEventListener(
        "wheel",
        handleWheelOutside,
        {
          capture: true,
          passive: true,
        },
      );

      document.addEventListener(
        "touchstart",
        handleTouchStartOutside,
        {
          capture: true,
          passive: true,
        },
      );

      document.addEventListener(
        "touchmove",
        handleTouchMoveOutside,
        {
          capture: true,
          passive: true,
        },
      );

      document.addEventListener(
        "touchend",
        handleTouchEnd,
        true,
      );

      document.addEventListener(
        "touchcancel",
        handleTouchEnd,
        true,
      );

      return () => {
        clearTimeout(wheelGestureTimer);

        document.removeEventListener(
          "mousedown",
          handleOutsideClick,
        );

        document.removeEventListener(
          "wheel",
          handleWheelOutside,
          true,
        );

        document.removeEventListener(
          "touchstart",
          handleTouchStartOutside,
          true,
        );

        document.removeEventListener(
          "touchmove",
          handleTouchMoveOutside,
          true,
        );

        document.removeEventListener(
          "touchend",
          handleTouchEnd,
          true,
        );

        document.removeEventListener(
          "touchcancel",
          handleTouchEnd,
          true,
        );
      };
    }, [closeDropdown, open]);

    useEffect(() => {
      if (!open) {
        return undefined;
      }

      updateDropdownWidth();

      const handleResize = () => {
        updateDropdownWidth();
      };

      window.addEventListener(
        "resize",
        handleResize,
      );

      return () => {
        window.removeEventListener(
          "resize",
          handleResize,
        );
      };
    }, [
      open,
      updateDropdownWidth,
    ]);

    useEffect(() => {
      if (disabled) {
        closeDropdown();
      }
    }, [
      closeDropdown,
      disabled,
    ]);

    const handleSelect = (countryItem) => {
      if (disabled) {
        return;
      }

      const countryCode =
        getCountryAlpha2(countryItem).toUpperCase();

      if (!countryCode) {
        return;
      }

      onChange(countryCode);
      closeDropdown();
    };

    const dropdownContent = (
      <div
        ref={dropdownRef}
        className={`country-select-profile__dropdown ${
          isMobile
            ? "country-select-profile__dropdown--mobile"
            : ""
        }`}
        style={{
          maxHeight: `${dropdownMaxHeight}px`,
        }}
        role="listbox"
      >
        {countries.map((countryItem) => {
          const countryCode =
            getCountryAlpha2(countryItem);

          const isSelected =
            countryCode === normalizedValue;

          return (
            <button
              type="button"
              key={
                countryItem.id ||
                countryItem.code ||
                countryCode
              }
              className={`country-select-profile__option ${
                isSelected
                  ? "country-select-profile__option--selected"
                  : ""
              }`}
              onClick={() =>
                handleSelect(countryItem)
              }
              role="option"
              aria-selected={isSelected}
            >
              <img
                className="country-select-profile__flag"
                src={getFlagUrl(countryItem)}
                alt=""
              />

              <span className="country-select-profile__label">
                {getCountryLabel(countryItem)}
              </span>
            </button>
          );
        })}
      </div>
    );

    return (
      <div
        ref={selectRef}
        className={`country-select-profile ${
          disabled
            ? "country-select-profile--disabled"
            : ""
        }`}
      >
        <button
          type="button"
          className={`country-select-profile__control ${
            showArrow ? "has-arrow" : ""
          } ${!selected ? "is-placeholder" : ""} ${
            error ? "is-error" : ""
          } ${open ? "is-open" : ""}`}
          onClick={toggleDropdown}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span className="country-select-profile__content">
            {selected ? (
              <>
                <img
                  className="country-select-profile__flag"
                  src={getFlagUrl(selected)}
                  alt=""
                />

                <span className="country-select-profile__label">
                  {getCountryLabel(selected)}
                </span>
              </>
            ) : (
              <>
                <CountryIcon className="country-select-profile__placeholder-icon" />

                <span className="country-select-profile__placeholder">
                  {placeholder}
                </span>
              </>
            )}
          </span>

          {showArrow && (
            <span
              className={`country-select-profile__arrow ${
                open
                  ? "country-select-profile__arrow--open"
                  : ""
              }`}
              aria-hidden="true"
            />
          )}
        </button>

        <Popper
          open={open && !disabled}
          anchorEl={selectRef.current}
          placement="bottom"
          strategy="absolute"
          className="country-select-profile__popper"
          style={{
            width: `${dropdownWidth}px`,
          }}
          modifiers={[
            {
              name: "eventListeners",
              enabled: true,
              options: {
                scroll: true,
                resize: true,
              },
            },
            {
              name: "offset",
              enabled: true,
              options: {
                offset: [0, dropdownGap],
              },
            },
            {
              name: "flip",
              enabled: false,
            },
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                padding: MOBILE_SIDE_OFFSET,
                mainAxis: true,
                altAxis: false,
                tether: false,
              },
            },
          ]}
        >
          {dropdownContent}
        </Popper>
      </div>
    );
  },
);

CountrySelectProfile.displayName =
  "CountrySelectProfile";

export default CountrySelectProfile;
